"use client";

import React, { useState, useEffect, Suspense } from "react";
import { usePathname } from "next/navigation";
import CompanyProfileSkeleton from "@/src/components/companies/CompanyProfileSkeleton";
import { getCompanyReports } from "@/src/app/hooks/companies.hooks.js";
import dynamic from "next/dynamic";
import { Button } from "@heroui/button";
import BoxSkeleton from "@/src/components/companies/BoxSkeleton";
import TradingViewWidgetSkeleton from "@/src/components/companies/TradingViewSkeleton";
//import CompanyHeaderSkeleton from "@/src/components/companies/HeaderSkeleton";
//import CompanyOverviewSkeleton from "@/src/components/companies/OverviewSkeleton";
import BreadcrumbsSkeleton from "@/src/components/companies/BreadcrumbsSkeleton";
import StockOwnershipSkeleton from "@/src/components/companies/StockOwnershipSkeleton";
import NewsGridSkeleton from "@/src/components/companies/NewsGridSkeleton";
import BoxReportSkeleton from "@/src/components/companies/BoxReportSkeleton";
import { useSession } from "next-auth/react";
import { ChevronUp } from "lucide-react";
import SideMenuSkeleton from "@/src/components/companies/SideMenuSkeleton";
import BoxSkeletonFinancials from "@/src/components/companies/BoxSkeletonFinancials";
import SidebarSkeleton from "@/src/components/companies/SidebarSkeleton";
//import StockValuationSkeleton from "@/src/components/companies/StockValuationSkeleton";
import CompanyOverview from "@/src/components/companies/CompanyOverview";
import CompanyHeader from "@/src/components/companies/CompayHeader";

const NewsSection = dynamic(
  () => import("@/src/components/companies/NewsSection"),
  {
    loading: () => <NewsGridSkeleton />,
  }
);

{
  /*const StockValuation = dynamic(
  () => import("@/src/components/companies/FairValue"),
  {
    loading: () => <StockValuationSkeleton />,
  }
);*/
}

const FinancialStatements = dynamic(
  () => import("@/src/components/companies/Financials"),
  {
    loading: () => <BoxSkeletonFinancials />,
  }
);

const StockPeersList = dynamic(
  () => import("@/src/components/companies/StockPeerList"),
  {
    loading: () => <BoxSkeleton />,
  }
);

const StockOwnershipTable = dynamic(
  () => import("@/src/components/companies/StockOwnership"),
  {
    loading: () => <StockOwnershipSkeleton />,
  }
);

const SidebarCompany = dynamic(
  () => import("@/src/components/companies/SidebarCompany"),
  {
    loading: () => <SidebarSkeleton />,
  }
);

// const CompanyHeader = dynamic(
//   () => import("@/src/components/companies/CompayHeader"),
//   {
//     loading: () => <CompanyHeaderSkeleton />,
//   }
// );

const Breadcrumbs = dynamic(
  () => import("@/src/components/companies/Breadcrumbs"),
  {
    loading: () => <BreadcrumbsSkeleton />,
  }
);

{
  /*const CompanyOverview = dynamic(
  () => import("@/src/components/companies/CompanyOverview"),
  {
    loading: () => <CompanyOverviewSkeleton />,
  }
);*/
}

const ReportSection = dynamic(
  () => import("@/src/components/companies/ReportSection"),
  {
    loading: () => <BoxReportSkeleton />,
  }
);

const TradingViewWidget = dynamic(
  () => import("@/src/components/companies/TradingViewWidget"),
  {
    loading: () => <TradingViewWidgetSkeleton />,
  }
);

const initialSections = [
  { id: "overview", label: "Overview" },
  { id: "research-reports", label: "Research Reports" },
  { id: "news", label: "News" },
  { id: "stockQuote", label: "Stock Quote" },
  { id: "financials", label: "Financials" },
  { id: "comparables", label: "Comparables" },
  { id: "ownership", label: "Ownership" },
];

export default function CompanyProfile() {
  const { data: session } = useSession();
  const pathname = usePathname();
  const ticker = pathname.split("/")[2]; // Assumes ticker is the third part of the path

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [realTimePrice, setRealTimePrice] = useState(null); // New state for real-time price
  const [activeSection, setActiveSection] = useState("overview");

  // Scroll up function
  const [showScroll, setShowScroll] = useState(false);

  // Fetching company data
  useEffect(() => {
    const fetchCompanyData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getCompanyReports(ticker);
        setCompanyData(response);
      } catch (error) {
        setError(error.message);
        console.error("Fetching error: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyData();

    // Scroll to top button logic
    const checkScrollTop = () => {
      if (!showScroll && window.pageYOffset > 200) {
        setShowScroll(true);
      } else if (showScroll && window.pageYOffset <= 200) {
        setShowScroll(false);
      }
    };

    window.addEventListener("scroll", checkScrollTop, { passive: true });
    return () => {
      window.removeEventListener("scroll", checkScrollTop, { passive: true });
    };
  }, [pathname, ticker, showScroll]);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRealTimePriceUpdate = (price) => {
    setRealTimePrice(price);
  };

  if (error) {
    return <div>Error loading company data: {error}</div>;
  }

  return (
    <div className="">
      {companyData ? (
        <div className="company-profile-page w-full md:p-2 xl:p-2 xl:container min-h-screen  mx-auto">
          {/* Breadcrumbs */}
          <div className="w-full mt-5 ml- mb-5 px-2 sm:px-2 lg:px-2">
            <Breadcrumbs companyData={companyData} />
          </div>

          {/* Scroll to Top Button */}
          {showScroll && (
            <div className="fixed bottom-20 right-4 z-50">
              <Button
                auto
                rounded
                shadow
                className=" bg-1B51CA text-white"
                onPress={scrollTop}
                endContent={<ChevronUp strokeWidth={1.5} absoluteStrokeWidth />}
              >
                Top
              </Button>
            </div>
          )}

          <div className="w-full px- sm:px-2 lg:px-2">
            {/* Sidebar - shown at the top on small and medium screens */}
            <div className="md:hidden">
              <SidebarCompany
                sections={initialSections}
                activeSection={activeSection}
                onSectionSelect={setActiveSection}
              />
            </div>

            {/* Header - shown after the sidebar on small and medium screens */}
            <div className="mt-4">
              <CompanyHeader
                ticker={ticker}
                session={session}
                company={companyData}
                onRealTimePriceUpdate={handleRealTimePriceUpdate} // Pass callback
              />
            </div>

            {/* Content Section */}
            <div className="flex flex-col md:flex-row mt-4">
              {/* Sidebar - hidden on small and medium screens */}
              <div className="hidden md:block md:w-1/5 sticky top-24 h-screen overflow-y-auto">
                <SidebarCompany
                  sections={initialSections}
                  activeSection={activeSection}
                  onSectionSelect={setActiveSection}
                />
              </div>

              {/* Main Content */}
              <div className="flex-1 md:pl-4 overflow-x-hidden">
                <CompanyOverview company={companyData} />

                <TradingViewWidget stockSymbol={ticker} />
                <ReportSection
                  companyId={ticker}
                  isCompanyPrivate={companyData.isPrivate}
                />
                {/*<StockValuation
                  realTimePrice={realTimePrice}
                  ticker={ticker}
                  token={session?.user.apiToken}
                />*/}

                <NewsSection ticker={ticker} />
                <FinancialStatements ticker={ticker} />

                <StockPeersList ticker={ticker} />

                <StockOwnershipTable ticker={ticker} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <CompanyProfileSkeleton />
      )}
    </div>
  );
}
